// components/executive/CoreModules/CoreModules.jsx
import { useNavigate } from 'react-router-dom';
import {
  MdOutlinePets,
  MdOutlineTrendingUp,
  MdOutlineBusinessCenter,
  MdOutlinePeople,
  MdOutlineAssignment,
  MdOutlineGavel,
  MdOutlineQrCodeScanner,
  MdOutlineMonitorHeart,
} from 'react-icons/md';
import { useAuth } from '../../../context/AuthContext';
import { coreModules } from './coreModulesConfig';
import { useCountUp } from '../../../hooks/useCountUp';
import { useModuleCounts } from '../../../hooks/useModuleCounts';
import { ROUTE_MODULES, canAccessModule } from '../../../config/rbacConfig';

const iconMap = {
  livestock: MdOutlinePets,
  markets: MdOutlineTrendingUp,
  infrastructure: MdOutlineBusinessCenter,
  workforce: MdOutlinePeople,
  planning: MdOutlineAssignment,
  conflicts: MdOutlineGavel,
  animalId: MdOutlineQrCodeScanner,
  animalHealth: MdOutlineMonitorHeart,
};

const footerLabelByRole = {
  R01: 'Last Updated',
  R02: 'Last Approved',
  R03: 'Last Submitted',
  R04: 'Last Validated',
  R05: 'Last Save Draft',
  R06: 'Last Updated',
};

const footerFieldByRole = {
  R01: 'lastUpdated',
  R02: 'lastApproved',
  R03: 'lastSubmitted',
  R04: 'lastValidated',
  R05: 'lastSavedDraft',
  R06: 'lastUpdated',
};

function getFooterMeta(roleId, module) {
  const label = footerLabelByRole[roleId] ?? 'Last Updated';
  const fieldName = footerFieldByRole[roleId] ?? 'lastUpdated';
  const value =
    module[fieldName] ??
    module.lastValidated ??
    module.lastUpdated ??
    module.lastSavedDraft ??
    module.lastSubmitted ??
    module.lastApproved ??
    '';

  return { label, value };
}

const ModuleCard = ({ module, roleId }) => {
  const navigate = useNavigate();
  const Icon = iconMap[module.iconKey] ?? MdOutlinePets;
  const { count, ref } = useCountUp(module.records, 1200);
  const footer = getFooterMeta(roleId, module);

  return (
    <div
      ref={ref}
      onClick={() => navigate(module.path)}
      className="
        relative bg-gray-50 rounded-xl border border-gray-100 shadow-sm
        p-4 flex flex-col gap-3 cursor-pointer
        transition-all duration-300 ease-out group
        hover:shadow-lg hover:shadow-green-100/50
        hover:border-green-200 hover:-translate-y-0.5
        hover:bg-gradient-to-br hover:from-white hover:to-green-50/30
        active:scale-[0.98] mb-6
      "
    >
      <div className="flex items-start">
        <div className="
          w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center
          transition-transform duration-300 group-hover:scale-110 group-hover:bg-green-100
        ">
          <Icon size={20} className="text-green-600 transition-colors group-hover:text-green-700" />
        </div>
      </div>

      <div className="flex flex-col gap-0.5">
        <h3 className="
          text-[13px] font-semibold text-gray-900 leading-snug
          transition-colors duration-200 group-hover:text-green-700
        ">
          {module.name}
        </h3>
        <p className="text-[9px] text-gray-400 leading-snug group-hover:text-gray-500 transition-colors">
          {module.description}
        </p>
      </div>

      <div className="border-t border-gray-100 pt-2 flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="text-[8px] text-gray-400">Records</span>
          <span className="text-[10px] font-bold text-gray-800 tabular-nums">
            {count.toLocaleString()}
          </span>
        </div>
        {footer.value && (
          <p className="text-[8px] text-gray-400 italic">
            {footer.label}: {footer.value}
          </p>
        )}
      </div>

      <div className="
        absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
        transition-opacity duration-300 pointer-events-none
        bg-gradient-to-r from-green-200/0 via-green-300/20 to-green-200/0
        blur-sm -z-10
      " />
    </div>
  );
};

const CoreModules = ({ filter, title = 'Core Modules' }) => {
  const { user, permissions } = useAuth();
  const roleId = user?.roleId ?? null;
  const filteredModules = Array.isArray(filter) && filter.length > 0
    ? coreModules.filter((m) => filter.includes(m.id))
    : coreModules;
  const visible = filteredModules
    .filter((mod) => {
      const moduleKey = ROUTE_MODULES[mod.path];
      return moduleKey ? canAccessModule(user, permissions, moduleKey) : true;
    });
  const counts = useModuleCounts(visible.map((mod) => mod.id));

  return (
    <section className="animate-fade-in-up" style={{ animationDelay: '100ms' }}>
      <h2 className="text-[15px] font-semibold text-gray-800 mt-12 mb-4">{title}</h2>
      {visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-500">
          No modules are available for this role and module scope yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {visible.map((mod, idx) => {
            const liveCount = counts[mod.id];
            const records = Number.isFinite(liveCount) ? liveCount : 0;

            return (
              <div
                key={mod.id}
                className="animate-fade-in-up"
                style={{ animationDelay: `${150 + idx * 80}ms` }}
              >
                <ModuleCard module={{ ...mod, records }} roleId={roleId} />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default CoreModules;
